import { useMutation, useQuery } from '@tanstack/react-query'
import { AXIOS_INSTANCE } from '../api'
import { queryClient } from '../store/provider/TanStackProvider'
import { ArgProps, GeneralResMock } from '../types/general.type'
import { ProductData, ProductIncDatas, ProductMeta } from '../types/product.type'

type Props = ArgProps & {
  stat?: boolean
  status?: 'expired' | 'expiring' | 'in_stock' | 'out_of_stock'
}
export const useProducts = (arg?: Props) => {
  const { page, query, id } = arg ?? {}
  const sort_dir = arg?.sort_dir || 'desc'
  const sort_by = arg?.sort_by || 'created_at'
  const limit = arg?.limit || 100
  const stat = arg?.stat ?? true
  const status = arg?.status ?? undefined

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      const params: Record<string, any> = {
        ...(page && { 'page[number]': page }),
        ...(limit && { 'page[size]': limit }),
        ...(sort_by && { sort_by }),
        ...(sort_dir && { sort_dir }),
        ...(query && { query }),
        ...(stat && { stat }),
        ...(status && { status })
      }

      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductData[], ProductIncDatas, ProductMeta>>(
        'products',
        { params }
      )
      return data
    },
    queryKey: ['products', { limit, page, query, sort_by, sort_dir, stat, status }]
  })

  const { data: singleProduct, isLoading: singleProductIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductData, ProductIncDatas>>(`products/${id}`)
      return data
    },
    queryKey: ['products', { id }]
  })

  const { mutateAsync: createProduct } = useMutation({
    mutationFn: async (formRec: any) => {
      const { data } = await AXIOS_INSTANCE.post('products', formRec, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })

  const { mutateAsync: updateProduct } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`products/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', { id }] })
    }
  })

  return {
    createProduct,
    included: data?.included,
    meta: data?.meta,
    productPagination: data?.meta?.pagination,
    products: data?.data,
    productsIsLoading: isLoading,
    singleProduct: singleProduct?.data,
    singleProductIncluded: singleProduct?.included,
    singleProductIsLoading,
    updateProduct
  }
}
